import { APPLICATION_VIEW_LABELS } from './constants';

const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const deduplicateChanges = (changes = []) => {
  const seen = new Set();
  const deduped = [];

  (changes || []).forEach((change) => {
    if (!change) return;
    const key = change.change_number || change.number || change.sys_id;
    if (key) {
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
    }
    deduped.push(change);
  });

  return deduped;
};

export const mergeSuccessStatus = (changesSources = []) => {
  const mergeCategory = (categoryKey) => {
    const mergedChanges = deduplicateChanges(
      changesSources.reduce((acc, source = {}) => {
        const categoryChanges = source.success_status?.[categoryKey]?.changes || [];
        return acc.concat(categoryChanges);
      }, [])
    );

    const fallbackCount = changesSources.reduce((acc, source = {}) => {
      return acc + (source.success_status?.[categoryKey]?.count || 0);
    }, 0);

    return {
      count: mergedChanges.length > 0 ? mergedChanges.length : fallbackCount,
      changes: mergedChanges
    };
  };

  return {
    successful_changes: mergeCategory('successful_changes'),
    failed_changes: mergeCategory('failed_changes'),
    ongoing_status_changes: mergeCategory('ongoing_status_changes')
  };
};

export const mergeOverdueChanges = (changesSources = []) => {
  const mergedChanges = deduplicateChanges(
    changesSources.reduce((acc, source = {}) => {
      const overdueChanges = source.overdue_changes?.changes || [];
      return acc.concat(overdueChanges);
    }, [])
  );

  const fallbackCount = changesSources.reduce((acc, source = {}) => {
    return acc + (source.overdue_changes?.count || 0);
  }, 0);

  return {
    count: mergedChanges.length > 0 ? mergedChanges.length : fallbackCount,
    changes: mergedChanges
  };
};

export const mergeConflictingChanges = (changesSources = []) => {
  const conflictGroups = [];
  const conflictGroupKeys = new Set();
  const collectedChanges = [];

  changesSources.forEach((source = {}) => {
    if (source?.conflicting_changes?.conflict_groups) {
      source.conflicting_changes.conflict_groups.forEach((group = {}) => {
        const key = group.group_id || JSON.stringify(group);
        if (key && conflictGroupKeys.has(key)) {
          return;
        }
        if (key) {
          conflictGroupKeys.add(key);
        }
        conflictGroups.push(group);
      });
    }
    if (source?.conflicting_changes?.changes) {
      collectedChanges.push(...source.conflicting_changes.changes);
    }
  });

  return {
    conflict_groups: conflictGroups,
    changes: deduplicateChanges(collectedChanges)
  };
};

const collectListChanges = (subscribedViaListChanges = {}) => {
  return Object.values(subscribedViaListChanges).filter(Boolean);
};

export const buildConsolidatedApplicationChanges = (data = {}) => {
  const ownedChanges = data.application_changes || {};
  const subscribedChanges = data.subscribed_application_changes || {};
  const listChanges = collectListChanges(data.subscribed_via_list_application_changes || {});
  const changeSources = [ownedChanges, subscribedChanges, ...listChanges];

  const mergeChangesByKey = (key) => {
    const merged = changeSources.reduce((acc, source = {}) => {
      const items = source[key] || [];
      return acc.concat(items);
    }, []);
    return deduplicateChanges(merged);
  };

  return {
    ongoing_changes: mergeChangesByKey('ongoing_changes'),
    past_changes: mergeChangesByKey('past_changes'),
    future_changes: mergeChangesByKey('future_changes'),
    success_status: mergeSuccessStatus(changeSources),
    overdue_changes: mergeOverdueChanges(changeSources),
    conflicting_changes: mergeConflictingChanges(changeSources)
  };
};

export const resolveChangesData = ({
  data,
  viewType,
  applicationView,
  platformName,
  portfolioName
}) => {
  if (!data) {
    return {
      changesData: null,
      incidentsInfo: null
    };
  }

  if (viewType === 'applications') {
    if (applicationView === 'consolidated') {
      return {
        changesData: buildConsolidatedApplicationChanges(data),
        incidentsInfo: data.application_incidents || null
      };
    }

    if (applicationView === 'owned') {
      return {
        changesData: data.application_changes || null,
        incidentsInfo: data.application_incidents || null
      };
    }

    if (applicationView === 'subscribed') {
      return {
        changesData: data.subscribed_application_changes || null,
        incidentsInfo: data.application_incidents || null
      };
    }

    if (applicationView?.startsWith('list:')) {
      const listName = applicationView.replace('list:', '');
      return {
        changesData: data.subscribed_via_list_application_changes?.[listName] || null,
        incidentsInfo: data.application_incidents || null
      };
    }
  }

  if (viewType === 'platforms') {
    const platformChanges = data.platform_changes || {};
    let changesData = platformName ? platformChanges[platformName] : null;

    if (!changesData && platformName) {
      const fallbackKey = Object.keys(platformChanges).find((key) =>
        key.includes(platformName) || platformName.includes(key)
      );
      changesData = fallbackKey ? platformChanges[fallbackKey] : null;
    }

    return {
      changesData: changesData || null,
      incidentsInfo: data.platform_incidents || null
    };
  }

  if (viewType === 'portfolios') {
    if (portfolioName && data.portfolio_changes?.[portfolioName]) {
      return {
        changesData: data.portfolio_changes[portfolioName],
        incidentsInfo: data.portfolio_incidents || null
      };
    }

    return {
      changesData: data.portfolio_changes || null,
      incidentsInfo: data.portfolio_incidents || null
    };
  }

  return {
    changesData: null,
    incidentsInfo: null
  };
};

export const transformChangesToTableData = (tableChanges = []) => {
  return tableChanges
    .map((change) => {
      const transformedItem = {
        'Change Number': change.change_number || 'N/A',
        'Configuration Item': change.configuration_item || 'N/A',
        Risk: change.risk_impact || 'N/A',
        'Short Description': change.short_description || 'N/A',
        'Planned Start Date': change.planned_start_date || 'N/A',
        'Planned End Date': change.planned_end_date || 'N/A',
        State: change.state || 'N/A',
        Type: change.type || 'N/A',
        'Assignment Group': change.assignment_group || 'N/A',
        'Requested By': change.requested_by || 'N/A',
        'Close Code': change.close_code || 'N/A',
        Category: change.change_type || 'N/A'
      };

      if (change.relation) {
        transformedItem.Relation = `${change.relation}`;
      }

      return transformedItem;
    })
    .filter((change) => change['Change Number'] !== 'N/A');
};

export const buildIncidentsAggregate = (incidentsInfo) => {
  if (!incidentsInfo) {
    return { p1: 0, p2: 0, p3: 0, p4: 0, total: 0 };
  }

  const p1 = incidentsInfo.p1_incidents?.length || 0;
  const p2 = incidentsInfo.p2_incidents?.length || 0;
  const p3 = incidentsInfo.p3_incidents?.length || 0;
  const p4 = incidentsInfo.p4_incidents?.length || 0;

  return {
    p1,
    p2,
    p3,
    p4,
    total: p1 + p2 + p3 + p4
  };
};

export const getApplicationOverviewCopy = ({ edit, applicationView, userName }) => {
  if (!edit) {
    const name = userName || 'the selected user';
    return `This is a comprehensive overview of the Changes occurring on all the applications ${name} owns along with their upstream dependencies.`;
  }

  if (applicationView === 'consolidated') {
    return 'This is a comprehensive overview of the Changes occurring on all applications you own, are subscribed to, and from subscription lists along with their upstream dependencies.';
  }

  if (applicationView === 'owned') {
    return 'This is a comprehensive overview of the Changes occurring on all the applications you own along with their upstream dependencies.';
  }

  if (applicationView === 'subscribed') {
    return 'This is a comprehensive overview of the Changes occurring on all applications you are subscribed to along with their upstream dependencies.';
  }

  if (applicationView?.startsWith('list:')) {
    const listName = applicationView.replace('list:', '');
    return `This is a comprehensive overview of the Changes occurring on applications from the "${listName}" subscription list along with their upstream dependencies.`;
  }

  return '';
};

export const getApplicationsBlankState = ({
  viewType,
  applicationView,
  userDetails = {},
  edit,
  userName
}) => {
  if (viewType !== 'applications') {
    return { showBlank: false, message: '' };
  }

  const {
    applications = [],
    subscribedApplications = [],
    subscribedViaListApplications = {}
  } = userDetails;

  const userLabel = edit ? 'You' : (userName || 'This user');

  if (applicationView === 'owned' && applications.length === 0) {
    return {
      showBlank: true,
      message: edit
        ? "You don't own any applications."
        : `${userLabel} doesn't own any applications.`
    };
  }

  if (applicationView === 'subscribed' && subscribedApplications.length === 0) {
    return {
      showBlank: true,
      message: edit
        ? "You are not subscribed to any applications."
        : `${userLabel} is not subscribed to any applications.`
    };
  }

  if (applicationView?.startsWith('list:')) {
    const listName = applicationView.replace('list:', '');
    const listApps = toArray(subscribedViaListApplications[listName]);
    if (listApps.length === 0) {
      return {
        showBlank: true,
        message: `No applications found in "${listName}".`
      };
    }
  }

  const hasOwned = applications.length > 0;
  const hasSubscribed = subscribedApplications.length > 0;
  const hasListApps = Object.keys(subscribedViaListApplications || {}).length > 0;

  if (
    applicationView === 'consolidated' &&
    !hasOwned &&
    !hasSubscribed &&
    !hasListApps
  ) {
    return {
      showBlank: true,
      message: edit
        ? "You don't have any applications."
        : `${userLabel} doesn't have any applications.`
    };
  }

  return { showBlank: false, message: '' };
};

export const getApplicationViewOptions = (userDetails = {}) => {
  const listOptions = Object.keys(userDetails.subscribedViaListApplications || {}).map((listName) => ({
    value: `list:${listName}`,
    label: listName
  }));

  return [
    { value: 'consolidated', label: APPLICATION_VIEW_LABELS.consolidated },
    { value: 'owned', label: APPLICATION_VIEW_LABELS.owned },
    { value: 'subscribed', label: APPLICATION_VIEW_LABELS.subscribed },
    ...listOptions
  ];
};
